import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import TrainingHeaders from './TrainingsHeader';
import { useGetToken } from '../../hooks/useGetToken';
import { fetchQualificationHeadersList, fetchTrainingsHeadersList, OfferingScope } from '../../services/TrainingsHeaderViewService';
import { intialTrainingsHeaderMapper, trainingsHeaderMapper } from '../helper';
import { makeStyles } from 'tss-react/mui';
import { useGlobalStateContext } from '../../context/globalState';


export type TrainingHeadersType = {
    name: string;
    id: number;
    passportTypeId: number;
    trainingTypeId: number;
    // The column is a single training, not an aggregate (type, level, group)
    isTraining?: boolean;
}

type QualificationHeadersType = {
    title: string;
    titleId: number;
}
export type QualificationListType = {
    qualificationHeaders: QualificationHeadersType[];
    titleKey: string;
    isAuthError?: boolean;
}

export type TrainingsListType = {
    trainingName: string;
    trainingId: number;
    passportTypeId: number;
    trainingTypeId: number;
}[]

interface TrainingsLevelViewProps {
    setPageLoader: (value: boolean) => void;
    addBreadcrumbItem: (value: string,key: string, id: number) => void;
    isOverallView: boolean;
    fetchUserQualificationStatus: (paramsKey: string, paramId: number | string) => void;
}


export const TRAINING_TYPE_IDS = {
    GLOBAL: 100,
    LOCAL: 101,
    METHOD: 102
}

const TrainingsLevelView = ({ setPageLoader, addBreadcrumbItem, fetchUserQualificationStatus }: TrainingsLevelViewProps) => {
    const [getToken] = useGetToken();
    const { t } = useTranslation();
    // Read from the global state
    const {
        setTrainingHeaders,
        country,
        setAlertState,
        setAlertMessage,
        breadcrumbs,
        isBreadcrumbClicked,
        setIsBreadcrumbClicked,
        userPreference: { languagePreference: lang },
        trainingTypes,
        selectedBusinessTypeId,
        regionId,
        frontlineId,
    } = useGlobalStateContext();
    const useStyles = makeStyles()((theme) => ({
        globalHeader: {
            backgroundColor: "#cbeaff",
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '17px',
            textAlign: 'center',
            letterSpacing: '0px',
            color: '#3D3D3D',
            opacity: 1,
        },
        localHeader: {
            backgroundColor: "	#f4b1c2",
            fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', textAlign: 'left'
        },
        methodHeader: {
            backgroundColor: " #8edac0",
            fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', textAlign: 'left'
        }
    }))
    const { classes } = useStyles();
    const [qualificationTypeList, setQualificationTypeList] = React.useState<TrainingHeadersType[]>([]);
    const [paramsKey, setParamsKey] = React.useState<string>('');
    const [prevParams, setPrevParams] = React.useState<{ key: string, value: number, language: string }>({ key: '', value: 0, language: 'en' });
    const [qualificationHeaders, setQualificationHeaders] = React.useState<QualificationListType>({ qualificationHeaders: [], titleKey: '' });
    const [isUnauthorized, setIsUnauthorized] = React.useState<boolean>(false);
    // Selected countries, used for the Local Requirements view
    const selectedCountryCodes = country && Array.isArray(country) && country.length > 0 ? country.join(',') : undefined;
    // Global Methods and Local Requirements columns show only the trainings
    // offered in the breadcrumb selection: the selected countries, else the
    // frontline, else the area. With nothing selected every offering in the
    // admin's scope is shown.
    const offeringScope = React.useMemo<OfferingScope>(() => ({
        countries: selectedCountryCodes,
        frontlineId: frontlineId ? String(frontlineId) : undefined,
        regionId: regionId ? String(regionId) : undefined,
    }), [selectedCountryCodes, frontlineId, regionId]);
    const offeringScopeKey = `${offeringScope.regionId || ''}|${offeringScope.frontlineId || ''}|${offeringScope.countries || ''}`;
    // The header level on screen, so a selection change can reload it in place
    const currentHeadersRequest = React.useRef<{ key?: string; value?: number } | null>(null);
    const previousOfferingScopeKey = React.useRef(offeringScopeKey);
    // Sequence number of the latest headers request. A response that is not
    // the latest is dropped, so a slow earlier request (e.g. for the previous
    // country selection) cannot overwrite the current headers. Every request
    // still releases its own loader: setPageLoader counts true/false calls,
    // so a skipped false would leave the spinner on.
    const latestHeadersRequest = React.useRef(0);


    useEffect(() => {
        if (qualificationTypeList.length > 0) {
            setTrainingHeaders(qualificationTypeList);
        }
    }, [qualificationTypeList])

    useEffect(() => {
        if (lang && lang !== prevParams.language) {
            fetchTrainingsHeaderViewData(prevParams.key, prevParams.value);
        }
    },[lang, prevParams.language]);

    useEffect(() => {
        // Resort the qualificationTypeList based on the new trainingTypes
        if (trainingTypes && Object.values(trainingTypes).some(val => val !== 0)) {
            setQualificationTypeList(intialTrainingsHeaderMapper(qualificationHeaders, trainingTypes));
            setPageLoader(false);
        }
    },[trainingTypes]);

    const fetchTrainingsHeaderViewData = async (queryParamKey?: string, queryParamValue?: number) => {
        const requestSeq = ++latestHeadersRequest.current;
        // This fetch already uses the current offering scope, so claim the scope
        // key synchronously (before any await) to stop a concurrent scope-reload
        // effect from superseding this navigation with the previous level's data
        previousOfferingScopeKey.current = offeringScopeKey;
        setPageLoader(true)
        const token = await getToken();
        let verifiedCountryId: string | undefined = undefined;
        // Lookup Local Requirement training type Id to verify the parameter training type is local or not
        if (queryParamKey === "trainingTypeId" && queryParamValue === trainingTypes.LOCAL_SAFETY) {
            verifiedCountryId = country && Array.isArray(country) && country.length > 0 ? country.join(',') : undefined;
        }
        currentHeadersRequest.current = { key: queryParamKey, value: queryParamValue };
        const qualificationHeaders: QualificationListType = await fetchQualificationHeadersList(token, queryParamKey, queryParamValue, verifiedCountryId, lang, selectedBusinessTypeId || undefined, offeringScope);
        if (requestSeq !== latestHeadersRequest.current) {
            setPageLoader(false); // superseded by a newer request: release only the loader
            return;
        }
        // Check for authorization error
        if (qualificationHeaders.isAuthError) {
            setIsUnauthorized(true);
            setPageLoader(false);
            return;
        }

        setPrevParams({ key: queryParamKey || '', value: queryParamValue ? queryParamValue : 0, language: lang || 'en' });
        if (qualificationHeaders?.qualificationHeaders?.length === 0) {
            setAlertState('error');
            const _alertMessage = queryParamKey === "trainingTypeId" && queryParamValue === trainingTypes.LOCAL_SAFETY ? t('error_messages.noLocalTrainings') : t('error_messages.noTrainingsError')
            setAlertMessage(_alertMessage);
            setPageLoader(false);
            return;
        }
        setParamsKey(qualificationHeaders?.titleKey);
        if (trainingTypes && Object.values(trainingTypes).some(val => val !== 0)) {
            setQualificationTypeList(intialTrainingsHeaderMapper(qualificationHeaders, trainingTypes));
            setPageLoader(false)
        }
    }

    
    // GET Inital Training Headers
    useEffect(() => {
        const trainingTypesLoaded = trainingTypes && Object.values(trainingTypes).some(val => val !== 0);

        // Skip if trainingTypes not loaded yet
        if (!trainingTypesLoaded) {
            return;
        }

        // Only fetch if:
        // 1. User clicked on breadcrumb navigation, OR
        // 2. Breadcrumbs is at root level (initial load or business type change reset)
        if (!isBreadcrumbClicked && breadcrumbs.length > 1) {
            // Breadcrumbs changed due to header expansion - expandHeaders already fetched, skip
            setIsBreadcrumbClicked(false);
            return;
        }
        if (breadcrumbs.length > 1) {
            fetchTrainingsHeaderViewData(breadcrumbs[breadcrumbs.length - 1].key, breadcrumbs[breadcrumbs.length - 1].value);
        } else {
            fetchTrainingsHeaderViewData();
        }
        if (breadcrumbs.length > 0) {
            fetchUserQualificationStatus(breadcrumbs[breadcrumbs.length - 1].key, breadcrumbs[breadcrumbs.length - 1].value);
        }
        setIsBreadcrumbClicked(false);
    }, [breadcrumbs, isBreadcrumbClicked, trainingTypes]);

    // expand the headers based on the column clicked
    const expandHeaders = async (paramId: number, title: string) => {
        if (paramsKey === "trainingId") {
            return;
        }
        const requestSeq = ++latestHeadersRequest.current;
        // This fetch already uses the current offering scope, so claim the scope
        // key synchronously (before any await) to stop a concurrent scope-reload
        // effect from superseding this navigation with the previous level's data
        previousOfferingScopeKey.current = offeringScopeKey;
        setPageLoader(true);
        const token = await getToken();
        let verifiedCountryId: string | undefined = undefined;
        // Lookup Local Requirement training type Id to verify the parameter training type is local or not
        if (paramsKey === "trainingTypeId" && paramId === trainingTypes.LOCAL_SAFETY) {
            verifiedCountryId = country && Array.isArray(country) && country.length > 0 ? country.join(',') : undefined;
        }
        const qualificationHeaders: QualificationListType = await fetchQualificationHeadersList(token, paramsKey, paramId, verifiedCountryId, lang, selectedBusinessTypeId || undefined, offeringScope);
        if (requestSeq !== latestHeadersRequest.current) {
            setPageLoader(false); // superseded by a newer request: release only the loader
            return;
        }

        // Check for authorization error
        if (qualificationHeaders.isAuthError) {
            setIsUnauthorized(true);
            setPageLoader(false);
            return;
        }

        if (qualificationHeaders.qualificationHeaders?.length === 0) {
            setAlertState('error');
            const _alertMessage = paramsKey === "trainingTypeId" && paramId === trainingTypes.LOCAL_SAFETY ? t('error_messages.noLocalTrainings') : t('error_messages.noTrainingsError')
            setAlertMessage(_alertMessage);
            setPageLoader(false);
            return;
        }
        setAlertState('');
        setAlertMessage('');
        currentHeadersRequest.current = { key: paramsKey, value: paramId };
        fetchUserQualificationStatus(paramsKey, paramId);
        setParamsKey(qualificationHeaders.titleKey);
        setQualificationTypeList(intialTrainingsHeaderMapper(qualificationHeaders, trainingTypes));
        addBreadcrumbItem(title, paramsKey, paramId);
        setPageLoader(false)
    }

    // GET the detailed Training Headers based on the column clicked
    const qualificationTypeClickHandler = async (
        qualificationTypeId: number,
        trainingName: string,
        trainingTypeId: number,
        expand: boolean = false
    ) => {
        if (!expand) {
            return;
        }
        setPageLoader(true)
        const token = await getToken();
        const verifiedCountryId = country && Array.isArray(country) && country.length > 0 ? country.join(',') : undefined;
        const response = await fetchTrainingsHeadersList(token, Number(qualificationTypeId), verifiedCountryId, selectedBusinessTypeId || undefined, offeringScope) as TrainingsListType;
        // Handle undefined or empty response
        const selectedQualificationHeaders: TrainingsListType = response || [];
        setQualificationTypeList(trainingsHeaderMapper(selectedQualificationHeaders));
        addBreadcrumbItem(trainingName, '', qualificationTypeId);
        setPageLoader(false)
    }

    // An area, frontline or country change alters which local and method
    // trainings are offered: reload the header level on screen without
    // touching the breadcrumbs
    useEffect(() => {
        if (previousOfferingScopeKey.current === offeringScopeKey) {
            return;
        }
        previousOfferingScopeKey.current = offeringScopeKey;
        const request = currentHeadersRequest.current;
        if (!request) {
            return;
        }
        const reloadHeaders = async () => {
            const requestSeq = ++latestHeadersRequest.current;
            const isLatest = () => requestSeq === latestHeadersRequest.current;
            setPageLoader(true);
            try {
                const token = await getToken();
                const isLocalView = request.key === "trainingTypeId" && request.value === trainingTypes.LOCAL_SAFETY;
                const response: QualificationListType = await fetchQualificationHeadersList(token, request.key, request.value, isLocalView ? selectedCountryCodes : undefined, lang, selectedBusinessTypeId || undefined, offeringScope);
                if (!isLatest()) {
                    return; // superseded by a newer request
                }
                if (response?.qualificationHeaders?.length > 0) {
                    setAlertState('');
                    setAlertMessage('');
                    setQualificationTypeList(intialTrainingsHeaderMapper(response, trainingTypes));
                } else {
                    // Nothing offered at this level for the new selection: drop the
                    // previous selection's columns instead of leaving them on screen
                    setQualificationTypeList([]);
                    setTrainingHeaders([]);
                    setAlertState('error');
                    setAlertMessage(isLocalView ? t('error_messages.noLocalTrainings') : t('error_messages.noTrainingsError'));
                }
            } finally {
                // Release this request's loader whether or not it was superseded
                setPageLoader(false);
            }
        };
        reloadHeaders();
    }, [offeringScopeKey]);

    if (isUnauthorized) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="60vh"
                flexDirection="column"
            >
                <Typography
                    variant="h5"
                    sx={{
                        color: '#D32F2F',
                        fontWeight: 600,
                        marginBottom: 2,
                        textAlign: 'center',
                    }}
                >
                    {t('adminDashboard.noAdGroupAccessMessage')}
                </Typography>
            </Box>
        );
    }

    return (
        <React.Fragment>
            {qualificationTypeList.length > 0 && <TrainingHeaders qualificationTypeList={qualificationTypeList} handleQualificationTypeClick={expandHeaders} />}
        </React.Fragment>
    );
};

export default TrainingsLevelView;